import React, { Component } from "react";
import MapGL, {
  NavigationControl,
  FullscreenControl,
  _MapContext as MapContext
} from "react-map-gl";
import DeckGL from "@deck.gl/react";
import { ScatterplotLayer } from "@deck.gl/layers";
import { FlyToInterpolator } from "deck.gl";
import Filter from "./components/Filter";
import { Client as Styletron } from "styletron-engine-atomic";
import { Provider as StyletronProvider } from "styletron-react";
import { BaseProvider, LightTheme } from "baseui";
import * as mbxGeocoding from "@mapbox/mapbox-sdk/services/geocoding";
import util from "./util/util";

import CodeModal from "./components/CodeModal";
//import * as querystring from "query-string";
import mapImage from "./images/map.png";

const engine = new Styletron();
// Set your mapbox token here
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOXKEY;

const geocodingClient = mbxGeocoding({
  accessToken: MAPBOX_TOKEN
});



const MS_PER_DAY = 8.64e7; // milliseconds in a day

//const QS = querystring.parse(window.location.search);

export class Map extends Component {
  constructor(props) {
    super(props);
    const timeRange = this._getTimeRange(props.data);
    const startFromRange = [timeRange[0], timeRange[0]];
    this.state = {
      codeModalOpen: false,
      timeRange,
      filterValue: startFromRange,
      hoveredObject: null,
      totalAffected: 0,
      totalBlackouts: 0,
      filteredData: null,
      citySearch: null,
      triggerAnimation: false,
      searchResults: null,
      searchTerm: null,
      searchBounds: null,
      bounds: {
        minLongitude: -126.58687072895174,
        minLatitude: 32.24470266040197,
        maxLongitude: -114.07192390885052,
        maxLatitude: 42.41475777783208
      },
      viewState: {
        latitude: 36.5,
        longitude: -120,
        zoom: 5,
        pitch: 0,
        bearing: 0
      },
      viewableArea: null
    };
    this._onHover = this._onHover.bind(this);
    this._renderTooltip = this._renderTooltip.bind(this);
    this.mapRef = React.createRef();
    this.myRef = React.createRef();
    this._onViewStateChange = this._onViewStateChange.bind(this);
    this._filterData = this._filterData.bind(this);

    this._checkIframe();

    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }

  _checkIframe() {
    if (window.location !== window.parent.location) {
      document.body.classList.add("in-iframe");
    }
  }

  _mapLoaded() {
    this.setState({ triggerAnimation: true });
  }

  _getTimeRange(data) {
    if (!data) {
      return null;
    }
    return data.reduce(
      (range, d) => {
        const t = d.RecordedDateTime / MS_PER_DAY;
        range[0] = Math.min(range[0], t);
        range[1] = Math.max(range[1], t);
        return range;
      },
      [Infinity, -Infinity]
    );
  }

  _onHover({ x, y, object }) {
    this.setState({ x, y, hoveredObject: object });
  }

  _calcStats(filteredData) {
    return filteredData.reduce(function(result, item) {
      return result + item.CustomersAffected;
    }, 0);
  }

  _onSearchSelect({ item }) {
    let lat_lng_from = [
      this.state.viewState.latitude,
      this.state.viewState.longitude
    ];
    let lat_lng_to = item.geometry.coordinates;

    let miles = this._getDistanceFromLatLonInMiles(
      lat_lng_from[0],
      lat_lng_from[1],
      lat_lng_to[1],
      lat_lng_to[0]
    );

    let time = miles * 10;

    this._setSearchState(
      {
        longitude: item.geometry.coordinates[0],
        latitude: item.geometry.coordinates[1],
        zoom: 12,
        transitionDuration: time < 500 ? 1000 : time,
        transitionInterpolator: new FlyToInterpolator(),
        searchTerm: item.place_name.split(",")[0]
      },
      {
        minLongitude: item.bbox[0],
        minLatitude: item.bbox[1],
        maxLongitude: item.bbox[2],
        maxLatitude: item.bbox[3]
      }
    );
  }

  _getDistanceFromLatLonInMiles(lat1, lon1, lat2, lon2) {
    var R = 6371; // Radius of the earth in km
    var dLat = this._deg2rad(lat2 - lat1); // deg2rad below
    var dLon = this._deg2rad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this._deg2rad(lat1)) *
        Math.cos(this._deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c; //KM to miles

    return d * 0.62137;
  }

  _deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  _setSearchState(viewState, searchBounds) {
    const filteredState = this._filterData(
      this.props.data,
      this.state.filterValue,
      searchBounds
    );

    this.setState({
      viewState: viewState,
      searchResults: null,
      searchBounds,
      searchTerm: viewState.searchTerm,
      ...filteredState
    });
  }

  _onClearSearch() {
    //get state without searchBountds
    const filteredState = this._filterData(
      this.props.data,
      this.state.filterValue,
      null
    );

    const viewState = {
      latitude: 36.5,
      longitude: -120,
      zoom: 5,
      pitch: 0,
      bearing: 0,
      transitionDuration: 2000,
      transitionInterpolator: new FlyToInterpolator()
    };
    this.setState({
      viewState,
      searchResults: null,
      searchTerm: null,
      searchBounds: null,
      ...filteredState
    });
  }

  _onViewStateChange({ viewState }) {
    //let's get the bounds of the browser and filter based on that
    const bounds = this.mapRef.current
      .getMap()
      .getBounds()
      .toArray();
    const bbox = bounds[0].concat(bounds[1]);
    const viewableArea = {
      minLongitude: bbox[0],
      minLatitude: bbox[1],
      maxLongitude: bbox[2],
      maxLatitude: bbox[3]
    };
    const filteredState = this._filterData(
      this.props.data,
      this.state.filterValue,
      this.state.searchBounds,
      this.state.viewableArea
    );

    const filterBrowserState = { ...filteredState };
    // lock viewable area in
    if (viewState.longitude < this.state.bounds.minLongitude) {
      viewState.longitude = this.state.bounds.minLongitude;
    } else if (viewState.longitude > this.state.bounds.maxLongitude) {
      viewState.longitude = this.state.bounds.maxLongitude;
    } else if (viewState.latitude < this.state.bounds.minLatitude) {
      viewState.latitude = this.state.bounds.minLatitude;
    } else if (viewState.latitude > this.state.bounds.maxLatitude) {
      viewState.latitude = this.state.bounds.maxLatitude;
    }

    // if (viewState.zoom < 5) {
    //   viewState.zoom = 5;
    // }
    this.setState({ viewState, viewableArea, ...filterBrowserState });
  }

  async _handleSearch(e) {
    if (e.target.value.length === 0) {
      this.setState({ searchResults: null });
    }

    if (e.target.value.length <= 2) {
      return;
    }

    const location = e.target.value;
    try {
      let response = await geocodingClient
        .forwardGeocode({
          bbox: [
            -124.92809137711585,
            32.21946768606247,
            -113.90822956652941,
            42.13352237319131
          ],
          proximity: [-119.65156698281965, 37.06611373065189],
          countries: ["US"],
          query: location,
          limit: 4,
          types: ["place"]
        })
        .send();
      if (
        response &&
        response.body &&
        response.body.features &&
        response.body.features.length
      ) {
        //hide SFO from search results
        //nevada
        let filteredSearchResults = response.body.features.filter(d => {
          return (
            d.id !== "place.13691893917314660" &&
            !d.place_name
              .split(",")[1]
              .toLowerCase()
              .includes("nevada") &&
            !d.place_name
              .split(",")[1]
              .toLowerCase()
              .includes("oregon") &&
            !d.place_name
              .split(",")[1]
              .toLowerCase()
              .includes("arizona")
          );
        });

        //check for san jose and center it.
        filteredSearchResults.forEach((item)=>{
          if(item.id ==="place.7704339974165690"){
            item.fart = 'ha'
            item.geometry.coordinates= [-121.8861, 37.3351];
          }
        });
        this.setState({ searchResults: filteredSearchResults });
      }
    } catch (err) {
      console.log("err: ", err.message);
    }
  }

  _setFilteredState(state) {
    this.setState(state);
  }

  _onFilterTime(response) {
    let { value } = response;
    const filterValue = value;
    const { data } = this.props;

    if (data) {
      const filteredState = this._filterData(data, filterValue);

      this._setFilteredState({ filterValue, ...filteredState });
    }
  }

  _filterData(
    data,
    timeFilter = this.state.filterValue,
    locationFilter = this.state.searchBounds,
    viewableArea = this.state.viewableArea
  ) {
    if (data) {
      let filteredData;
      if (timeFilter) {
        const filterStart = timeFilter[0];
        const filterEnd = timeFilter[1];
        filteredData = data.filter(d => {
          let rowTime = new Date(d.RecordedDateTime).getTime() / MS_PER_DAY;
          return rowTime >= filterStart && rowTime <= filterEnd;
        });
      }
      if (locationFilter) {
        // eslint-disable-next-line
        filteredData = filteredData.filter(d => {
          if (
            d.longitude >= locationFilter.minLongitude &&
            d.longitude <= locationFilter.maxLongitude &&
            d.latitude >= locationFilter.minLatitude &&
            d.latitude <= locationFilter.maxLatitude
          ) {
            return d;
          }
        });
      }
      if (viewableArea && locationFilter === null) {
        // eslint-disable-next-line
        filteredData = filteredData.filter(d => {
          if (
            d.longitude >= viewableArea.minLongitude &&
            d.longitude <= viewableArea.maxLongitude &&
            d.latitude >= viewableArea.minLatitude &&
            d.latitude <= viewableArea.maxLatitude
          ) {
            return d;
          }
        });
      }
      let totalAffected = this._calcStats(filteredData);

      return {
        filteredData,
        totalAffected,
        totalBlackouts: filteredData.length
      };
    }
  }

  _renderLayers() {
    const { data } = this.props;
    const { filteredData } = this.state;
    if (data) {
      let sp_layer = new ScatterplotLayer({
        id: "Outages",
        data: filteredData,
        opacity: 0.09,
        radiusScale: 30,
        wrapLongitude: true,
        radiusMinPixels: 1,
        radiusMaxPixels: 30,
        getPosition: d => {
          return [d.longitude, d.latitude];
        },
        getRadius: d => {
          return d.CustomersAffected / 100;
        },
        getFillColor: d => {
          return [86, 86, 86];
        },

        pickable: true,
        onHover: this._onHover
      });
      return [sp_layer];
    }
  }

  _renderTooltip() {
    let { x, y, hoveredObject } = this.state;
    const winWidth = window.innerWidth;
    const covered = winWidth - x;
    if (covered < 180) {
      x = x - 180;
    }
    return (
      hoveredObject && (
        <div className="tooltip" id="tooltip" style={{ top: y, left: x }}>
          <div>
            <b>City: </b>
            <span>{hoveredObject.CityName}</span>
          </div>
          <div>
            <b>Date of blackout: </b>
            <span>
              {new Date(hoveredObject.RecordedDateTime).toDateString()}
            </span>
          </div>
          <div>
            <b>Customers impacted: </b>
            <span>
              {hoveredObject.CustomersAffected.toString().replace(
                /\B(?=(\d{3})+(?!\d))/g,
                ","
              )}
            </span>
          </div>
        </div>
      )
    );
  }

  _handleOpenModal() {
    this.setState({ codeModalOpen: true });
  }
  _handleCloseModal() {
    this.setState({ codeModalOpen: false });
  }
  render() {
    const {
      mapStyle = "mapbox://styles/samcreate/ck3hx9eu815o61cqdym13exx9"
    } = this.props;
    const {
      timeRange,
      filterValue,
      totalAffected,
      totalBlackouts,
      triggerAnimation,
      searchResults,
      viewState,
      searchTerm,
      codeModalOpen
    } = this.state;

    const deck_height = util.isMobile() ? "100%" : "calc(100% - 70px)";

    return (
      <StyletronProvider value={engine}>
        <BaseProvider theme={LightTheme}>
          <div className="app-container">
            <div
              className={"map-container " + (util.isFirefox() ? "firefox" : "")}
            >
              <DeckGL
                ref={this.myRef}
                layers={this._renderLayers()}
                viewState={viewState}
                onViewStateChange={this._onViewStateChange}
                controller={true}
                width="100vw"
                height={deck_height}
                ContextProvider={MapContext.Provider}
              >
                {!util.isMobile() && (
                  <div className="nav">
                    <div className="fullscreen">
                      <FullscreenControl
                        container={document.querySelector("body")}
                      />
                    </div>
                    <NavigationControl showCompass={false} />
                  </div>
                )}

                <MapGL
                  ref={this.mapRef}
                  reuseMaps
                  mapStyle={mapStyle}
                  preventStyleDiffing={true}
                  mapboxApiAccessToken={MAPBOX_TOKEN}
                  attributionControl={false}
                  onLoad={this._mapLoaded.bind(this)}
                ></MapGL>
                {this._renderTooltip}
              </DeckGL>
            </div>
            <Filter
              MS_PER_DAY={MS_PER_DAY}
              filterValue={filterValue}
              timeRange={timeRange}
              triggerAnimation={triggerAnimation}
              totalBlackouts={totalBlackouts}
              totalAffected={totalAffected}
              searchResults={searchResults}
              onSearchChange={this._handleSearch.bind(this)}
              onFilterChange={this._onFilterTime.bind(this)}
              onSearchSelect={this._onSearchSelect.bind(this)}
              onClearSearch={this._onClearSearch.bind(this)}
              searchTerm={searchTerm}
            />

            

            <div className="disclaimer-container">
              <p>
                The tracker covers outages for 12 major utilities that occurred
                between October 15, 2017 and December 31, 2019. The data, originally collected by <a href="https://poweroutage.us/" target="_blank" rel="noopener noreferrer">PowerOutage.us</a>, has been
                organized to show only blackouts that affected at least 100
                customers and to combine blackouts that occurred on the same day
                in the same city into one event. Customers impacted is an aggregated count of the homes or businesses affected by the blackouts shown. Because of the map’s geocoding, in some cases a blackout event in one city is attributed to another. In rare cases, this might produce a double count.{" "}
                <a href="#embed" onClick={this._handleOpenModal.bind(this)}>
                  Embed this map
                </a>{" "}
                onto your site.
              </p>
            </div>
            <CodeModal
              isOpen={codeModalOpen}
              onModalClose={this._handleCloseModal.bind(this)}
            />
            <a
              href="#open-new-window"
              target="_blank"
              className="map-image-link"
            >
              <img
                src={mapImage}
                className="map-image"
                alt="open in a new window"
              />
            </a>
          </div>
        </BaseProvider>
      </StyletronProvider>
    );
  }
}